import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { useGameStore, subscribeEvents, SPEED_SECONDS_PER_TICK } from "../app/gameStore";
import { forecastBadgesFor, type ForecastBadge } from "./hazardTells";
import { HardwareNode } from "./hardware/HardwareNode";
import { Pipe } from "./hardware/Pipe";
import { PacketsInFlight } from "./hardware/PacketsInFlight";
import { FxLayer, useFxPool } from "./fx/FxLayer";
import { fxForEvent, makeResolver } from "./fx/registry";

/**
 * Draws the current snapshot (hardware + pipes) and turns the driver's event
 * stream into visual effects. This is the seam the whole art-first plan rests
 * on: it reads only GridSnapshot and SimEvent, so the real engine drops in
 * behind it without a single change here.
 */
export function Rig() {
  const snapshot = useGameStore((s) => s.snapshot);
  const speed = useGameStore((s) => s.speed);
  const tick = useGameStore((s) => s.tick);

  const { instances, spawn, expire } = useFxPool();
  const accumulator = useRef(0);

  // Resolved once per snapshot rather than per node: the forecast is a handful
  // of entries, but the lookup runs across every piece of hardware on the board.
  const badgesByTarget = useMemo(() => {
    const byTarget = new Map<string, ForecastBadge>();
    for (const badge of forecastBadgesFor(snapshot)) byTarget.set(badge.targetId, badge);
    return byTarget;
  }, [snapshot]);

  // Compute outgoing edges for each node (spec 0024: valve routing).
  const outgoingEdgesByNode = useMemo(() => {
    const byNode = new Map<string, string[]>();
    for (const edge of snapshot.edges) {
      const edges = byNode.get(edge.fromNodeId) ?? [];
      edges.push(edge.id);
      byNode.set(edge.fromNodeId, edges);
    }
    return byNode;
  }, [snapshot]);

  // One subscription funnels BOTH real driver events and debug-panel events
  // into the same FX path, so anything fired by hand looks exactly as it will
  // in a real run.
  useEffect(() => {
    const resolveFor = () => makeResolver(useGameStore.getState().snapshot);
    return subscribeEvents((event) => {
      spawn(fxForEvent(event, resolveFor()));
    });
    // spawn is stable enough for this prototype; re-subscribing per render
    // would drop events mid-tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useFrame((_, delta) => {
    const secondsPerTick = SPEED_SECONDS_PER_TICK[speed];
    if (!Number.isFinite(secondsPerTick)) return;
    accumulator.current += delta;
    if (accumulator.current >= secondsPerTick) {
      accumulator.current = 0;
      tick();
    }
  });

  return (
    <group>
      {snapshot.edges.map((edge) => (
        <Pipe key={edge.id} edge={edge} forecast={badgesByTarget.get(edge.id) ?? null} />
      ))}
      {snapshot.nodes.map((node) => (
        <HardwareNode
          key={node.id}
          node={node}
          forecast={badgesByTarget.get(node.id) ?? null}
          outgoingEdgeIds={outgoingEdgesByNode.get(node.id) ?? []}
        />
      ))}
      {/* Drawn from the snapshot rather than from packet.moved: a packet's
          position is state, and state that exists only for the length of an
          effect leaves a paused board empty. */}
      <PacketsInFlight snapshot={snapshot} />
      <FxLayer instances={instances} onExpire={expire} />
    </group>
  );
}
